import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/table-row.default.js';
import * as wireframeSkin from './skins/table-row.wireframe.js';
import '../checkbox/run-checkbox.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunTableRow extends RunElement {
  #skin = null;
  #value = undefined;
  #selected = false;
  #disabled = false;
  #rendering = false;

  /** @type {import('../table/run-table.js').RunTable|null} */
  _parentTable = null;
  _boundHandleCheckboxChange = this._handleCheckboxChange.bind(this);

  static get observedAttributes() {
    return ['skin', 'value', 'selected', 'disabled'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'skin':
        this.#skin = newValue;
        this.requestUpdate();
        break;
      case 'value':
        this.#value = newValue;
        this.requestUpdate();
        break;
      case 'selected':
        this.#selected = newValue !== null;
        this.requestUpdate();
        break;
      case 'disabled':
        this.#disabled = newValue !== null;
        this.requestUpdate();
        break;
    }
  }

  get skin() {
    return this.#skin;
  }

  set skin(value) {
    const coerced = value == null ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced === null) {
      this.removeAttribute('skin');
    } else {
      this.setAttribute('skin', coerced);
    }
    this.requestUpdate();
  }

  get value() {
    return this.#value;
  }

  set value(v) {
    const coerced = v == null ? undefined : String(v);
    if (this.#value === coerced) return;
    this.#value = coerced;
    if (coerced === undefined) {
      this.removeAttribute('value');
    } else {
      this.setAttribute('value', coerced);
    }
    this.requestUpdate();
  }

  get selected() {
    return this.#selected;
  }

  set selected(v) {
    const coerced = Boolean(v);
    if (this.#selected === coerced) return;
    this.#selected = coerced;
    this.toggleAttribute('selected', coerced);
    this.requestUpdate();
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(v) {
    const coerced = Boolean(v);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }

    // Re-attach checkbox listener after each render (innerHTML re-creates the DOM node)
    const checkbox = this.shadowRoot.querySelector('run-checkbox');
    if (checkbox) {
      checkbox.removeEventListener('change', this._boundHandleCheckboxChange);
      checkbox.addEventListener('change', this._boundHandleCheckboxChange);
    }
  }

  _handleCheckboxChange(_e) {
    if (this._parentTable != null && typeof this._parentTable._handleRowCheckboxChange === 'function') {
      this._parentTable._handleRowCheckboxChange(this);
    }
  }

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }
}

if (!customElements.get('run-table-row')) {
  customElements.define('run-table-row', RunTableRow);
}

export { RunTableRow };
export default RunTableRow;
